import {isNil} from 'ramda';

import {Nullish} from 'shared/web';

import {AlphaCatalogue} from '../api/digitalCertificateApi';

type AlphaCatalogueLabelParams = {
  propertyName: string;
  alphaCatalogue: AlphaCatalogue;
  vehicleType: string | Nullish;
  key: string | Nullish;
};

const findLabel = (entry: AlphaCatalogue[number], key: string): string | undefined =>
  entry.keys?.find((propertyKey) => propertyKey.const_key === key)?.labels?.[0]?.label;

const STYLE_PREFIX_REGEX =
  /^(CAR|VAN|MOTORCYCLE|TRUCK|BUS|CARAVAN|SEMI_TRAILER|TRAILER|AGRICULTURE_MACHINE|CONSTRUCTION_MACHINE|OTHER)STYLE_/;

/**
 * Derives the catalogue property_name from a style const_key.
 * E.g. "VANSTYLE_VAN" → "van_style", "CARSTYLE_SEDANS" → "car_style",
 * "MOTORCYCLESTYLE_CHOPPER" → "motorcycle_style".
 *
 * Returns null when the key doesn't match a known vehicle style prefix.
 */
const deriveStylePropertyName = (constKey: string): string | null => {
  const match = STYLE_PREFIX_REGEX.exec(constKey);
  if (isNil(match)) {
    return null;
  }
  return `${match[1].toLowerCase()}_style`;
};

export const getAlphaCatalogueLabel = (params: AlphaCatalogueLabelParams): string | Nullish => {
  if (isNil(params.key)) {
    return params.key;
  }

  // Direct lookup: match vehicle type + property name
  const directEntry = params.alphaCatalogue?.find(
    (property) =>
      property.vehicle_type === params.vehicleType && property.property_name === params.propertyName
  );

  const directLabel = directEntry ? findLabel(directEntry, params.key) : undefined;
  if (!isNil(directLabel)) {
    return directLabel;
  }

  // Fallback: Alpha data can contain style const keys from a different vehicle type
  // (e.g. VANSTYLE_VAN in passenger-car context). Derive the correct property_name
  // from the const_key prefix and look it up directly.
  const derivedPropertyName = deriveStylePropertyName(params.key);
  if (!isNil(derivedPropertyName) && derivedPropertyName !== params.propertyName) {
    const fallbackEntry = params.alphaCatalogue?.find(
      (property) => property.property_name === derivedPropertyName
    );
    if (!isNil(fallbackEntry)) {
      const fallbackLabel = findLabel(fallbackEntry, params.key);
      if (!isNil(fallbackLabel)) {
        return fallbackLabel;
      }
    }
  }

  return params.key;
};
